import React, { useState, useRef, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare, User, Building, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { sendContactEmailViaBackend, validateEmail, ContactFormData } from '../services/backendEmailService';
import { clientService } from '../services/clientService';
import { notificationService } from '../services/notificationService';

// Variable global para prevenir duplicados
let lastContactSubmitTime = 0;
const CONTACT_SUBMIT_COOLDOWN = 5000; // 5 segundos (aumentado)

const Contact = () => {
  const isSubmittingRef = useRef(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    ruc: '',
    industry: '',
    message: '',
    service: '',
    position: ''
  });
  
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check for pre-selected service from sessionStorage
  useEffect(() => {
    const selectedService = sessionStorage.getItem('selectedService');
    if (selectedService) {
      setFormData(prev => ({
        ...prev,
        service: selectedService
      }));
      // Clear the stored service after using it
      sessionStorage.removeItem('selectedService');
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const now = Date.now();
    
    // Prevent double submission - multiple checks con useRef
    if (isSubmittingRef.current || isSubmitting) {
      console.log('🚫 Ya se está enviando, ignorando clic duplicado');
      return;
    }
    
    // Cooldown check
    if (now - lastContactSubmitTime < CONTACT_SUBMIT_COOLDOWN) {
      console.log('🚫 En cooldown, ignorando clic rápido');
      notificationService.warning('Espere un momento', 'Por favor, espera 5 segundos antes de enviar otra solicitud.');
      return;
    }
    
    // Reset status
    setSubmitStatus('idle');
    
    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      notificationService.warning('Campos incompletos', 'Por favor, complete los campos obligatorios: Nombre, Email y Mensaje.');
      return;
    }
    
    // Validate email format
    if (!validateEmail(formData.email)) {
      notificationService.warning('Email inválido', 'Por favor, ingrese un email válido.');
      return;
    }
    
    // Update last submit time IMMEDIATELY y refs
    lastContactSubmitTime = now;
    isSubmittingRef.current = true;
    console.log('🚀 Iniciando envío de formulario de contacto');
    setIsSubmitting(true);
    
    try {
      // Send email using backend
      const emailSuccess = await sendContactEmailViaBackend(formData);
      
      // Add or find customer in database
      const customerData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        ruc: formData.ruc,
        industry: formData.industry,
        notes: `Contacto desde formulario web - ${formData.service ? `Servicio: ${formData.service}` : 'Consulta general'}`
      };
      
      let customer = null;
      let customerSuccess = false;
      
      try {
        customer = await clientService.findOrCreateCustomer(customerData);
        customerSuccess = !!customer;
        console.log('Cliente procesado:', customer);
      } catch (error) {
        console.error('Error al procesar cliente:', error);
      }
      
      // No crear cotización para correos de contacto, solo guardar el cliente
      
      if (emailSuccess || customerSuccess) {
        setSubmitStatus('success');
        // Reset form on success
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          ruc: '',
          industry: '',
          message: '',
          service: '',
          position: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const handleEmergencyCall = () => {
    // In a real app, this would initiate a phone call
    notificationService.info('Llamada de emergencia', 'Llamando a emergencia: +51 953 759 419\n\nEn una aplicación real, esto abriría la aplicación de teléfono o marcaría automáticamente.');
  };

  const handleEmergencyWhatsApp = () => {
    // In a real app, this would open WhatsApp
    notificationService.info('WhatsApp de emergencia', 'Abriendo WhatsApp de emergencia\n\nEn una aplicación real, esto abriría WhatsApp con el número de emergencia pre-configurado.');
  };



  return (
    <section id="contact" className="section-padding bg-white">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-accent-100 text-accent-700 rounded-full text-sm font-medium mb-6">
            Contacto
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Estamos para
            <span className="text-gradient"> Ayudarte</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Contáctanos para una consulta personalizada y descubre cómo podemos 
            optimizar tus operaciones industriales.
          </p>
        </div>


        {/* Contact Form and Map */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  Envíanos un Mensaje
                </CardTitle>
                <CardDescription>
                  Completa el formulario y nos pondremos en contacto contigo a la brevedad.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Primera fila: Nombre Completo, Cargo */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre Completo *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Tu nombre completo"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cargo
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="text"
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Tu cargo en la empresa"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Segunda fila: Email, Teléfono */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="tu@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="+51 953 759 419"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tercera fila: Empresa, RUC */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Empresa
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Nombre de tu empresa"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        RUC
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="ruc"
                          value={formData.ruc}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Número de RUC"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Cuarta fila: Industria, Servicio de Interés */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industria
                      </label>
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Selecciona tu industria</option>
                        <option value="papelera">Industria Papelera</option>
                        <option value="pesquera">Industria Pesquera</option>
                        <option value="alimentaria">Industria Alimentaria</option>
                        <option value="agroindustrial">Agroindustria</option>
                        <option value="manufactura">Manufactura</option>
                        <option value="otra">Otra</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Servicio de Interés
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Selecciona un servicio</option>
                        <option value="mantenimiento">Mantenimiento Industrial</option>
                        <option value="instalacion">Instalación y Montaje</option>
                        <option value="consultoria">Consultoría Técnica</option>
                        <option value="repuestos">Repuestos y Accesorios</option>
                        <option value="inspeccion">Inspección y Certificación</option>
                        <option value="capacitacion">Capacitación Técnica</option>
                      </select>
                    </div>
                  </div>

                  {/* Quinta fila: Mensaje */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mensaje *
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                        placeholder="Describe tus necesidades..."
                      ></textarea>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-primary text-lg py-4"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Enviar Mensaje
                      </>
                    )}
                  </Button>
                  
                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-center">
                        ¡Mensaje enviado con éxito! Nos pondremos en contacto contigo a la brevedad.
                      </p>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-center">
                        Hubo un error al enviar tu mensaje. Por favor, inténtalo nuevamente más tarde.
                      </p>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-bold text-gray-900">
                  Información de Contacto
                </CardTitle>
                <CardDescription>
                  Ponte en contacto con nosotros para recibir asesoría personalizada.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="border-l-4 border-primary-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">
                    Teléfono
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">
                    +51 953 759 419
                  </p>
                  <p className="text-gray-600 text-sm">
                    Lun-Vie: 8:00 AM - 6:00 PM
                  </p>
                </div>
                <div className="border-l-4 border-primary-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">
                    Email
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">
                    ventas@atomcorporations.com
                  </p>
                  <p className="text-gray-600 text-sm">
                    Respuesta en 24 horas
                  </p>
                </div>
                <div className="border-l-4 border-primary-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">
                    Dirección
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">
                    Av. Industrial 1234, Lima, Perú
                  </p>
                  <p className="text-gray-600 text-sm">
                    Visítanos en nuestra oficina
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Map Placeholder */}
            <div className="bg-gray-100 rounded-xl h-64 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">Mapa Interactivo</p>
                <p className="text-sm text-gray-500">
                  Encuentra nuestra oficina principal
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="mt-16 bg-red-50 border-2 border-red-200 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-red-800 mb-4">
            🚨 Emergencias 24/7
          </h3>
          <p className="text-red-700 mb-6 max-w-2xl mx-auto">
            Para emergencias industriales fuera del horario comercial, 
            estamos disponibles 24/7 para atenderte.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg" onClick={handleEmergencyCall}>
              <Phone className="w-5 h-5 mr-2" />
              Llamar Ahora: +51953759419
            </Button>
            <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-8 py-4 text-lg" onClick={handleEmergencyWhatsApp}>
              WhatsApp de Emergencia
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
